import { z } from 'zod';
import { EventName } from '../ingest/common.ts';
import { PropertyCondition } from './triggers.ts';

/**
 * When a Content Card is created for a profile (BB-63, CARD-005). `first_impression` (default, Braze "At first
 * impression (Recommended)"): at the profile's next feed sync, after the audience is checked again and the content is
 * rendered with the data of that moment. `step_entry`: right when the send or the Canvas step reaches the profile.
 * The mode is fixed once the campaign or Canvas is launched.
 */
export const CARD_CREATION_MODES = ['first_impression', 'step_entry'] as const;
export const CardCreationMode = z.enum(CARD_CREATION_MODES);
export type CardCreationMode = z.infer<typeof CardCreationMode>;
export const DEFAULT_CARD_CREATION_MODE: CardCreationMode = 'first_impression';

/** Why an eligible profile did not get its card (`card_skipped{reason}`); `withdrawn` — the card was withdrawn before it was created (BB-65). */
export const CARD_SKIP_REASONS = ['audience_no_longer_matches', 'template_aborted', 'template_error', 'payload_too_large', 'payload_invalid', 'profile_unavailable', 'withdrawn'] as const;
export type CardSkipReason = (typeof CARD_SKIP_REASONS)[number];

/** Cards materialized in one feed sync at most; the rest wait for the next sync. */
export const CARD_MATERIALIZE_PER_SYNC = 50;

/**
 * A removal trigger of a card (BB-65, CARD-005): a custom event of the profile, with optional property conditions
 * (segment operators), withdraws the profile's card of that message. Same shape as a `custom_event` trigger of
 * action-based delivery, so one matcher serves both. Purchases are not ingested (DAT-012), so there is no purchase trigger.
 */
export const CardRemovalTrigger = z.strictObject({ kind: z.literal('custom_event'), event: EventName, propertyFilters: z.array(PropertyCondition).max(10).default([]) });
export type CardRemovalTrigger = z.infer<typeof CardRemovalTrigger>;
export const CARD_REMOVAL_TRIGGERS_MAX = 10;
export const CardRemovalTriggers = z.array(CardRemovalTrigger).max(CARD_REMOVAL_TRIGGERS_MAX);

/** Which cards a withdrawal takes (BB-65): every card of a message, of campaigns and Canvases with a tag, or with an extras value. */
export const CardWithdrawSelector = z.union([
  z.strictObject({ messageApiId: z.uuid() }),
  z.strictObject({ tagId: z.uuid() }),
  z.strictObject({ extrasKey: z.string().trim().min(1).max(255), extrasValue: z.string().max(1000) }),
]);
export type CardWithdrawSelector = z.infer<typeof CardWithdrawSelector>;
/** A withdrawal reason ends up in `card_withdrawn{reason}` and on the card; `removal_event:<event>` for removal triggers. */
export const CardWithdrawReason = z.string().trim().min(1).max(200);

/** `POST /server/v1/cards/withdraw` (scope `profiles.write`): withdraw one profile's cards, e.g. once VCG's backend sees the bonus used. */
export const ServerWithdrawCardsBody = z.strictObject({ externalId: z.string().min(1).max(128), selector: CardWithdrawSelector, reason: CardWithdrawReason });
export type ServerWithdrawCardsBody = z.infer<typeof ServerWithdrawCardsBody>;
/** Created cards withdrawn and pending (not yet created) cards that will never be created. A repeated call answers zeros. */
export interface WithdrawCardsResult {
  withdrawn: number;
  pendingSkipped: number;
}

/**
 * Migration bridge (BB-65): VCG's Canvases log `Content Card Removal { campaign: <value> }` through User Update; while
 * this environment rule is set, such an event withdraws the profile's cards whose extras `extrasKey` equal that value.
 * `null` or unset — off (the goal once Canvases use removal triggers or the withdraw action).
 */
export const LegacyRemovalEvent = z.strictObject({
  name: EventName.default('Content Card Removal'),
  property: z.string().trim().min(1).max(255).default('campaign'),
  extrasKey: z.string().trim().min(1).max(255).default('bonus-campaign'),
});
export type LegacyRemovalEvent = z.infer<typeof LegacyRemovalEvent>;
